/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { TelemetryEventName, TelemetryEvents } from 'src/core/services/telemetry/types';

/**
 * This is a wrapper function for logger logEvent.
 * The main idea is to look up the input event name
 * and validate it exist in the TelemetryEvent type.
 * Once validated then it fire Logger.logEvent with
 * typed arguments corresponding to this eventName
 */
export const getEventLogger = (activitySource?: string) => {
    const logger = getLogger(activitySource);

    const log = <TN extends TelemetryEventName>(
        eventName: TN,
        ...args: TelemetryEvents[TN] extends undefined ? [never?] : [TelemetryEvents[TN]]
    ) => {
        logger.logEvent(eventName, { args });
    };

    const start = <TN extends TelemetryEventName>(
        eventName: TN,
        ...args: TelemetryEvents[TN] extends undefined ? [never?] : [TelemetryEvents[TN]]
    ) => {
        logger.startEvent(eventName, { args });
    };

    const end = <TN extends TelemetryEventName>(
        eventName: TN,
        ...args: TelemetryEvents[TN] extends undefined ? [never?] : [TelemetryEvents[TN]]
    ) => {
        logger.endEvent(eventName, false, { args });
    };

    const error = <TN extends TelemetryEventName>(
        errorObj: Error,
        ...args: TelemetryEvents[TN] extends undefined ? [never?] : [TelemetryEvents[TN]]
    ) => {
        logger.error(errorObj, { args });
    };

    return {
        log,
        start,
        end,
        error
    };
};
